/**
 * IMPORTANT: The appConfig defaults in this file must be kept in sync with:
 * /home/timum/Timum/core/public/javascripts/react/timum_react_components/src/components/publicationView/appConfigDefaults.js
 *
 * When changing appConfig defaults in either file, mirror the changes in the other.
 */
'use strict';
import React from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import ScopableDialog from './components/base/ScopableDialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import ClosableDialogTitle from './components/base/ClosableDialogTitle';
import { Container } from '@mui/material';

const borderRadius = 6;
const fontSizeBase = 14;
const fontFamily = '"Figtree","Helvetica Neue", Helvetica, Arial, sans-serif';
// const lineHeightBase = 1.428571429;
// const lineHeightComputed = Math.floor(fontSizeBase * lineHeightBase);

const fontSizeH1 = Math.floor(fontSizeBase * 2.6); // 36
const fontSizeH2 = Math.floor(fontSizeBase * 2.3); // 32
const fontSizeH3 = Math.floor(fontSizeBase * 1.9); // 26
const fontSizeH4 = Math.floor(fontSizeBase * 1.5); // 21
const fontSizeH5 = Math.floor(fontSizeBase * 1.15); // 16
const fontSizeH6 = Math.floor(fontSizeBase); // 14

/**
 * Standard field names that are part of the default appConfig.
 * These fields can be toggled on/off but not deleted entirely.
 */
export const STANDARD_FIELD_NAMES = [
  'firstName',
  'lastName',
  'email',
  'mobile',
  'message',
  'agbs',
  'locale',
];

/*
 * Default configuration
 *
 */
export const primary = {
  appConfig: {
    host: 'https://www.timum.de',

    /*
     * Valid values for resource_channel attribute:
     *  RESOURCE_PUBLIC,
     *  RESOURCE_EXCLUSIVE
     *  RESOURCE_REFERENCE
     *  CALENDAR_PUBLIC
     */
    resource_channel: 'RESOURCE_REFERENCE',

    // either fullCalendar or pureListView or detailsListView
    calendarFrontend: 'detailsListView',

    // settings which change the behaviour of a particular view
    calendarFrontendOptions: {
      condensedView: {
        // if true, the resource selection will always be rendered as a dialog
        forceResourceSelectorDialog: false,
        // if true, renders bookables from all resources side-by-side when they overlap in time
        multiResourceMode: false,
      },
    },

    // if set to true and in a multi resource scenario, show an option which loads all bookables of all resources.
    // details are hidden when this option is selected
    allResourcesOption: true,

    // whether the confirmation view is closeable once the booking was successfull.
    allowCloseOnBooking: false,

    // whether the cancel view is closeable once it has been opened. If false, it only closes after previous appointment was cancelled.
    allowCloseOnCancel: false,

    /* 
    Controls where dialogs from the embed are rendered. 
    When enabled, all dialogs are confined to the embed’s designated container (identified via container or containerId) and are clipped/positioned within its bounds, behaving as part of the embed. 
    When disabled, dialogs are rendered at the page level and overlay the entire site, behaving like native site modals. 
    fullScreen respects this setting: with scoping enabled it fills the container; with scoping disabled it fills the browser viewport.
    */
    constrainDialogsToContainer: true,

    // whether 'powered by timum' can be hidden or not. Needs premium plan.
    hideTimumFooter: false,

    // whether bookingjs should hide itself when no customer identifying pData props can be read from the config or the url.
    hiddenForAnonymous: false,

    // send custom field content as comma sperarated, concatenated string via message field to backend
    sendCustomValuesInMessage: false,

    gatherAddressData: false,

    height: 500,

    fields: {
      firstName: {
        index: 0,
        title: 'fields.firstName',
        validation: yup.string().required('validation.field_required'), // <- compare with key in localisation
      },
      lastName: {
        index: 1,
        title: 'fields.lastName',
        validation: yup.string().required('validation.field_required'),
      },
      email: {
        index: 2,
        title: 'fields.email',
        format: 'email',
        type: 'text',
        validation: yup
          .string()
          .email('validation.email_field_must_be_valid')
          .required('validation.field_required'),
      },
      mobile: {
        index: 3,
        title: 'fields.mobile',
        type: 'phoneNumber',
        isRequired: false,
        defaultCountry: 'DE',
        preferredCountries: ['DE', 'CH', 'AT'],
        // validation: is in built and ignored
      },
      message: {
        index: 4,
        title: 'fields.message',
        type: 'textarea',
        validation: yup.string().max(600),
        limit: 600,
      },
      agbs: {
        index: 5,
        title: 'fields.accept_timum_privacy',
        type: 'checkbox',
        validation: yup
          .boolean()
          .required('validation.field_required')
          .test(
            'privacyAccepted',
            'validation.privacy_field_required',
            (value) => value === true,
          ),
      },
      locale: {
        index: 6,
        preventRendering: true,
      },
    },
    interfaces: {
      UnkownBookingPage: ({
        open,
        onClose,
        constrainDialogsToContainer,
        children,
      }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
          >
            <ClosableDialogTitle
              onClose={onClose}
              position={constrainDialogsToContainer ? 'left' : 'right'}
            />
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },
      IdentifiedBookingPage: ({
        open,
        onClose,
        constrainDialogsToContainer,
        children,
      }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
          >
            <ClosableDialogTitle
              onClose={onClose}
              position={constrainDialogsToContainer ? 'left' : 'right'}
            />
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },

      // is used by unknownBookingPage and identifiedBookingPage in reaction to a successfull booking
      ConfirmationPage: (props) => {
        const {
          open,
          onClose,
          allowCloseOnBooking,
          constrainDialogsToContainer,
          children,
        } = props;
        return (
          <ScopableDialog
            open={open}
            onClose={() => {
              if (allowCloseOnBooking) {
                onClose();
              }
            }}
            enableScoping={constrainDialogsToContainer}
          >
            {allowCloseOnBooking && (
              <ClosableDialogTitle
                onClose={onClose}
                position={constrainDialogsToContainer ? 'left' : 'right'}
              />
            )}
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },

      // is used by unknownBookingPage when typed in email belongs to an active timum user.
      // (Security feature, preventing unknwons to fill our users calenedars with arbitrary appointmens.)
      LoginPage: ({ open, onClose, constrainDialogsToContainer, children }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
          >
            <DialogContent>{children}</DialogContent>
          </ScopableDialog>
        );
      },
      CancelPage: ({
        open,
        onClose,
        constrainDialogsToContainer,
        allowCloseOnCancel,
        // timeslot,
        // productName,
        // resourceName,
        // onCancelSuccess,
        children,
      }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={() => {
              if (allowCloseOnCancel) {
                onClose();
              }
            }}
            enableScoping={constrainDialogsToContainer}
          >
            {allowCloseOnCancel && (
              <ClosableDialogTitle
                onClose={onClose}
                position={constrainDialogsToContainer ? 'left' : 'right'}
              />
            )}
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },
      ResourcePage: ({
        mobileOpen,
        mobileOnClose,
        constrainDialogsToContainer,
        shouldDisplay,
        height,
        children,
      }) => {
        return (
          <>
            <ScopableDialog
              open={mobileOpen}
              onClose={mobileOnClose}
              enableScoping={constrainDialogsToContainer}
            >
              <DialogContent
                sx={{
                  pl: { xs: 2, sm: 3, md: 4 },
                  pr: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <Container maxWidth="sm" sx={{ p: 0 }}>
                  {children}
                </Container>
              </DialogContent>
            </ScopableDialog>
            {shouldDisplay && (
              <Grid
                xs="auto"
                item
                container
                display={{ xs: 'none', sm: 'flex' }}
                wrap="nowrap"
                sx={{
                  mr: 2,
                  maxHeight: height,
                  boxSizing: 'border-box',
                }}
              >
                <Grid item xs="auto">
                  {children}
                </Grid>
                <Grid item xs={1}>
                  <Divider orientation="vertical" variant="middle" />
                </Grid>
              </Grid>
            )}
          </>
        );
      },

      ProductPage: ({
        open,
        onClose,
        children,
        constrainDialogsToContainer,
      }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
            // bad for assistive technologies
            // but websites jump to auto opened dialog if enabled.
            disableAutoFocus
            disableEnforceFocus
            aria-modal="true"
            role="dialog"
            aria-label="Appointment booking"
          >
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="md" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },
      ErrorDialog: ({ open, onClose, constrainDialogsToContainer }) => {
        const { t } = useTranslation();
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {t('reservation_failed.title')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {t('reservation_failed.message')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} autoFocus>
                Ok
              </Button>
            </DialogActions>
          </ScopableDialog>
        );
      },
    },
    callbacks: {},
  },
  muiTheme: {
    palette: {
      primary: {
        main: '#337ab7',
      },
      secondary: { main: '#777' },
      success: {
        main: '#5cb85c',
      },
      warning: { main: '#f0ad4e' },
      error: { main: '#d9534f' },
      info: { main: '#5bc0de' },
    },
    shape: { borderRadius: borderRadius },
    typography: {
      fontFamily: fontFamily,
      fontFaces: [
        {
          family: 'Figtree',
          style: 'normal',
          weight: '100 900',
          display: 'swap',
          src: [
            {
              url: 'https://s3.eu-central-1.amazonaws.com/custom.timum.de/__templates/std_font/Figtree-VariableFont_wght.woff2',
              format: 'woff2',
            },
          ],
        },
        {
          family: 'Figtree',
          style: 'italic',
          weight: '100 900',
          display: 'swap',
          src: [
            {
              url: 'https://s3.eu-central-1.amazonaws.com/custom.timum.de/__templates/std_font/Figtree-Italic-VariableFont_wght.woff2',
              format: 'woff2',
            },
          ],
        },
      ],
      // Override MUI's pxToRem so all internally-generated sizes emit px
      // instead of rem. This makes the widget immune to the host page's
      // root font-size — critical for a shadow-DOM-embedded component
      // that can't control the document-level <html> font-size.
      pxToRem: (size) => `${size}px`,
      fontSize: fontSizeBase,
      h1: {
        fontWeight: 600,
        lineHeight: 1.1,
        fontSize: `${fontSizeH1}px`,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 500,
        lineHeight: 1.15,
        fontSize: `${fontSizeH2}px`,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 500,
        lineHeight: 1.2,
        fontSize: `${fontSizeH3}px`,
        letterSpacing: '-0.005em',
      },
      h4: {
        fontWeight: 500,
        lineHeight: 1.25,
        fontSize: `${fontSizeH4}px`,
      },
      h5: {
        fontWeight: 500,
        lineHeight: 1.3,
        fontSize: `${fontSizeH5}px`,
      },
      h6: {
        fontWeight: 500,
        lineHeight: 1.4,
        fontSize: `${fontSizeH6}px`,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      // body1: {
      //   fontFamily: fontFamily,
      //   fontWeight: 400, // Normal weight for body text
      //   lineHeight: 1.5, // Optimal for reading
      //   fontSize: `${fontSizeBody1}rem`,
      // },
      // body2: {
      //   fontFamily: fontFamily,
      //   fontWeight: 400,
      //   lineHeight: 1.4,
      //   fontSize: `${fontSizeBody2}rem`,
      // },
      // caption: {
      //   fontFamily: fontFamily,
      //   fontWeight: 400,
      //   lineHeight: 1.3,
      //   fontSize: `${fontSizeCaption}rem`,
      //   letterSpacing: '0.03em', // Slight positive tracking for small text
      // },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: '#bfbfbf #ffffff',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: '#ffffff',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: '#bfbfbf',
              minHeight: 8,
              width: 3,
              border: '5px solid #ffffff',
            },
            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
              {
                backgroundColor: '#8f8f8f',
              },
            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
              {
                backgroundColor: '#8f8f8f',
              },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
              {
                backgroundColor: '#8f8f8f',
              },
            '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
              backgroundColor: '#ffffff',
            },
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          hideBackdrop: true,
          maxWidth: 'md',
          fullWidth: true,
        },
        styleOverrides: {
          paper: {
            boxShadow:
              '0px 0px 8px 0px rgba(0,0,0,0.08), 0px 0px 16px 2px rgba(0,0,0,0.05)',
            outline: '1px solid rgba(0,0,0,0.08)',
          },

        },
      },
      // borderRadius overrides removed for Button, Paper, OutlinedInput —
      // those components read from theme.shape.borderRadius natively.
      // ListItem and ListItemButton do NOT read shape.borderRadius in MUI v6,
      // so they need explicit overrides. Using a callback to read from the
      // theme dynamically, so postMessage-triggered theme changes take effect.
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'inherit',
            boxShadow: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            // marginTop: lineHeightComputed,
            // marginBottom: lineHeightComputed / 2,
          },
          h2: {
            // marginTop: lineHeightComputed,
            // marginBottom: lineHeightComputed / 2,
          },
          h3: {
            // marginTop: lineHeightComputed,
            // marginBottom: lineHeightComputed / 2,
          },
          h4: {
            // marginTop: lineHeightComputed / 2,
            // marginBottom: lineHeightComputed / 2,
          },
          h5: {
            // marginTop: lineHeightComputed / 2,
            // marginBottom: lineHeightComputed / 2,
          },
          h6: {
            // marginTop: lineHeightComputed / 2,
            // marginBottom: lineHeightComputed / 2,
          },
        },
      },
    },
  },
  fcConfig: {
    initialView: 'timeGridWeek',
    useCustomTimumCss: true,
    displayEventEnd: false,
    scrollTimeReset: false,
    scrollTime: '09:00:00',
    slotLabelInterval: '00:30:00',
    slotLabelFormat: { hour: 'numeric', minute: '2-digit' },
    views: {
      timeGridWeek: {
        slotMinTime: '07:00:00',
        slotMaxTime: '21:00:00',
        allDaySlot: false,
      },
    },
  },
};

// var customerFieldsNativeFormats = {
//   email: {
//     format: 'email',
//   },
//   comment: {
//     format: 'textarea',
//   },
//   phone: {
//     format: 'tel',
//   },
// };

// Preset: timeDateFormat = '24h-dmy-mon'
// var timeDateFormat24hdmymon = {
//   ui: {
//     booking_date_format: 'D. MMMM YYYY',
//     booking_time_format: 'HH:mm',
//   },
//   fullcalendar: {
//     timeFormat: 'HH:mm',
//     firstDay: 1,
//     views: {
//       basic: {
//         columnFormat: 'dddd D.M.',
//       },
//       agenda: {
//         columnFormat: 'ddd D.M.',
//         slotLabelFormat: 'HH:mm',
//       },
//     },
//   },
// };

// Preset: timeDateFormat = '12h-mdy-sun'
// var timeDateFormat12hmdysun = {
//   ui: {
//     booking_date_format: 'MMMM D, YYYY',
//     booking_time_format: 'h:mma',
//   },
//   fullcalendar: {
//     timeFormat: 'h:mma',
//     firstDay: 0,
//     views: {
//       basic: {
//         columnFormat: 'dddd M/D',
//       },
//       agenda: {
//         columnFormat: 'ddd M/D',
//         slotLabelFormat: 'h:mma',
//       },
//     },
//   },
// };

// Preset: availabilityView = 'agendaWeek'
// var availabilityViewAgendaWeek = {
//   fullcalendar: {
//     header: {
//       left: '',
//       center: '',
//       right: 'today, prev, next',
//     },
//     defaultView: 'agendaWeek',
//   },
// };

// Preset: availabilityView = 'listing'
// var availabilityViewListing = {
//   fullcalendar: {
//     header: {
//       left: '',
//       center: '',
//       right: '',
//     },
//     defaultView: 'listing',
//   },
// };
